import { ChatFollowUp } from '../../services/chat.service';

/** `send` posts the translated text as-is; `prefill` only drops it into the input for the employee
 * to finish. The chip label is always exactly the text that gets sent/prefilled. `intent` goes
 * along with a sent follow-up so AI Agents doesn't have to guess it from the wording. */
export interface ChatSuggestion {
  id: string;
  key: string;
  params?: Record<string, string>;
  action: 'send' | 'prefill';
  intent?: string;
}

const SKU_LOOKUP: ChatSuggestion = { id: 'sku-lookup', key: 'chat_widget.suggestions.skuLookup', action: 'prefill' };
const STOCK_OF: ChatSuggestion = { id: 'stock', key: 'chat_widget.suggestions.stock', action: 'prefill' };
const PRICE_OF: ChatSuggestion = { id: 'price', key: 'chat_widget.suggestions.price', action: 'prefill' };
const SIMILAR_TO: ChatSuggestion = { id: 'similar-to', key: 'chat_widget.suggestions.similarTo', action: 'prefill' };

/** Industry-neutral, so all prefill: a chip without a product keyword would search on nothing. */
export const DEFAULT_CHAT_SUGGESTIONS: ChatSuggestion[] = [SKU_LOOKUP, STOCK_OF, PRICE_OF, SIMILAR_TO];

/** Stock lookup and purchase request/order screens - stock is what is being decided on there. */
export const STOCK_SCREEN_URL = /^\/(?:stock\/search|procurement\/purchase-(?:request|order))(?:[/?]|$)/;
export const STOCK_SCREEN_SUGGESTIONS: ChatSuggestion[] = [STOCK_OF, SKU_LOOKUP, PRICE_OF];

/** Selling price, price history and flash sale screens. */
export const PRICE_SCREEN_URL = /^\/(?:configuration\/price|goods\/price-history|marketing\/flash-sale)(?:[/?]|$)/;
export const PRICE_SCREEN_SUGGESTIONS: ChatSuggestion[] = [PRICE_OF, SKU_LOOKUP, STOCK_OF];

/** The brand edit screen carries the brand id as the `id` query param (group 1). */
export const BRAND_SCREEN_URL = /^\/goods\/brand\/edit\?(?:.*&)?id=([^&#]+)/;

/** Chips for the brand the employee is looking at. */
export function brandChatSuggestions(brandName: string): ChatSuggestion[] {
  return [
    { id: 'brand-products', key: 'chat_widget.suggestions.brandProducts', params: { brand: brandName.trim() }, action: 'send' },
    STOCK_OF,
    SKU_LOOKUP,
  ];
}

/** Chips about the products in the latest reply - built from its cards, no extra request. */
export function contextChatSuggestions(cards: { name: string }[]): ChatSuggestion[] {
  const names = [...new Set(cards.map((card) => shortName(card.name ?? '')).filter(Boolean))];
  if (names.length === 0) {
    return [];
  }
  return [
    { id: 'similar-shown', key: 'chat_widget.suggestions.similar', params: { name: names[0] }, action: 'send' },
    ...(names.length > 1
      ? [{
          id: 'compare',
          key: 'chat_widget.suggestions.compare',
          params: { first: names[0], second: names[1] },
          action: 'send' as const,
        }]
      : []),
    ...names.slice(1, 3).map((name, index) => ({
      id: `similar-shown-${index + 2}`,
      key: 'chat_widget.suggestions.similar',
      params: { name },
      action: 'send' as const,
    })),
  ];
}

/** Chips about a reply / all chips shown under it. */
export const MAX_REPLY_SUGGESTIONS = 3;
export const MAX_SUGGESTIONS = 5;

/** Same chip twice (same text) is shown once, first one wins. */
export function uniqueSuggestions(suggestions: ChatSuggestion[]): ChatSuggestion[] {
  const seen = new Set<string>();
  return suggestions.filter((suggestion) => {
    const id = suggestion.key + JSON.stringify(suggestion.params ?? {});
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/** Matches the SKU view/edit screens; group 1 is the SKU id used to load its name/price. */
export const SKU_SCREEN_URL = /^\/goods\/sku\/(?:ordinary|package)\/(?:view|edit)\/(\d+)/;

/** Matches the SPU view/edit screens, which carry the SPU id as the `id` query param (group 1). */
export const SPU_SCREEN_URL = /^\/goods\/spu\/(?:ordinary|package)\/(?:view|edit)\?(?:.*&)?id=(\d+)/;

const SHORT_NAME_MAX_WORDS = 5;

/** Chips for the SKU the employee is looking at - all still plain product-search questions. */
export function skuChatSuggestions(name: string, salesPrice: number | null | undefined): ChatSuggestion[] {
  const short = shortName(name);
  return [
    { id: 'similar', key: 'chat_widget.suggestions.similar', params: { name: short }, action: 'send' },
    ...(salesPrice
      ? [{
          id: 'similar-under',
          key: 'chat_widget.suggestions.similarUnder',
          params: { name: short, price: priceCeiling(salesPrice) },
          action: 'send' as const,
        }]
      : []),
    { id: 'sku-lookup', key: 'chat_widget.suggestions.skuLookup', action: 'prefill' },
  ];
}

/** An SPU has no single price, and search returns only the best SKU per SPU, so "its versions"
 * can't be asked - only similar products. */
export function spuChatSuggestions(name: string): ChatSuggestion[] {
  return [
    { id: 'similar', key: 'chat_widget.suggestions.similar', params: { name: shortName(name) }, action: 'send' },
    { id: 'sku-lookup', key: 'chat_widget.suggestions.skuLookup', action: 'prefill' },
  ];
}

/** Chips under the latest reply, as AI Agents suggested them from what was asked for (the topic,
 * not just the first card). Each one carries its intent: CHEAPER narrows the same request, MORE
 * asks for products not shown yet. */
export function followUpSuggestions(followUps: ChatFollowUp[]): ChatSuggestion[] {
  return followUps.flatMap((followUp): ChatSuggestion[] => {
    const topic = followUp.topic ?? '';
    if (!topic) {
      return [];
    }
    if (followUp.kind === 'CHEAPER' && followUp.price) {
      return [{
        id: 'cheaper',
        key: 'chat_widget.suggestions.topicUnder',
        params: { topic: topic.charAt(0).toUpperCase() + topic.slice(1), price: priceLabel(followUp.price) },
        action: 'send',
        intent: 'REFINE',
      }];
    }
    if (followUp.kind === 'MORE') {
      return [{ id: 'more', key: 'chat_widget.suggestions.moreOfTopic', params: { topic }, action: 'send', intent: 'MORE' }];
    }
    if (followUp.kind === 'ANY_PRICE') {
      return [{
        id: 'any-price',
        key: 'chat_widget.suggestions.anyPrice',
        params: { topic: topic.charAt(0).toUpperCase() + topic.slice(1) },
        action: 'send',
        intent: 'REFINE',
      }];
    }
    return [];
  });
}

/** AI Agents sends round price steps (200000, 1000000) - worded the way its price parser reads. */
function priceLabel(amount: number): string {
  return amount >= 1_000_000 ? `${amount / 1_000_000} triệu` : `${amount / 1000}k`;
}

/** Long SPU names make noisy search text and overflowing chips - the first few words carry the
 * product type/brand, which is what "similar" needs. */
function shortName(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, SHORT_NAME_MAX_WORDS)
    .join(' ')
    .replace(/[\s,;:\-–(]+$/, '');
}

/** Rounds UP to a friendly step (100k below 1 million, whole millions above) so the SKU's own
 * price range is still included; both formats are what AI Agents' price-ceiling parser understands. */
function priceCeiling(amount: number): string {
  const thousands = Math.ceil(amount / 100_000) * 100;
  if (thousands < 1000) {
    return `${thousands}k`;
  }
  return `${Math.ceil(amount / 1_000_000)} triệu`;
}

/** `text` in front of what was typed, after dropping every leading `prefixes` match, so clicking
 * another prefill chip (or the same one again) swaps the prefix instead of stacking them. */
export function withPrefill(typed: string, text: string, prefixes: string[]): string {
  const trimmed = prefixes.map((prefix) => prefix.trim()).filter(Boolean);
  let rest = typed.trim();
  let stripped = true;
  while (stripped) {
    stripped = false;
    for (const prefix of trimmed) {
      if (rest.startsWith(prefix)) {
        rest = rest.slice(prefix.length).trim();
        stripped = true;
      }
    }
  }
  return text + rest;
}
